<template>
  <box-node>
    <div class="d-flex flex-column gap-4">
      <div class="d-flex flex-column gap-2">
        <div class="d-flex flex-row gap-2">
          <span class="font-weight-medium">Tratamento</span>
          <medicine-prescription-filter v-model="filter" />
        </div>
      </div>
      <div class="d-flex flex-column gap-2">
        <expandable-box-node v-for="(treatments, group) in treatments" :key="group" small>
          <template #title>
            <div class="d-flex flex-row gap-2 font-size-14 font-weight-medium">
              <div v-if="selectedQuantityPerGroup[group] > 0" class="d-flex flex-row align-center gap-1">
                <v-icon dense color="#406611"> mdi-check-circle </v-icon>
                <span style="color: #406611">
                  {{ selectedQuantityPerGroup[group] }}
                </span>
              </div>
              <span>
                {{ group }}
              </span>
            </div>
          </template>
          <div class="d-flex flex-column divide-y">
            <div class="d-flex flex-column" v-for="treatment in treatments" :key="treatment.id">
              <div class="d-flex flex-row align-center gap-2">
                <v-checkbox
                  v-model="selectedTreatments"
                  :value="treatment.id"
                  :key="treatment.id"
                  dense
                  hide-details
                  @change="(value) => toggleWarningIfPossible(treatment.medicine.name, value.includes(treatment.id))"
                  :label="treatment.medicine.name"
                  class="custom-checkbox py-2"
                />
                <v-btn
                  v-if="!!treatmentsWarnings[treatment.medicine.name]"
                  class="small-btn-icon"
                  color="warning"
                  icon
                  small
                  @click="toggleWarningIfPossible(treatment.medicine.name, true)"
                >
                  <v-icon>mdi-information-outline</v-icon>
                </v-btn>
              </div>
              <v-expand-transition>
                <div v-if="selectedTreatments.includes(treatment.id)">
                  <div class="pl-8 pb-4 d-flex flex-row gap-4 justify-space-between">
                    <select-component
                      v-if="!!treatmentPosologies[treatment.id]?.length"
                      v-model="selectedTreatmentsPosologies[treatment.id]"
                      :items="treatmentPosologies[treatment.id]"
                      itemValue="id"
                      itemText="prescription"
                      returnObject
                    />
                    <simple-text-input-component
                      v-else
                      v-model="selectedTreatmentsPosologies[treatment.id].prescription"
                      label="Posologia"
                    />

                    <v-btn
                      class="new-btn"
                      color="primary"
                      outlined
                      :disabled="isEmpty(getPrintTreatmentBody(treatment.id))"
                      @click="openDialog('printingTemplate', { treatment })"
                    >
                      Imprimir receita
                    </v-btn>
                  </div>
                </div>
              </v-expand-transition>
            </div>
          </div>
        </expandable-box-node>
      </div>
    </div>

    <template v-for="componentSlug in uniqueTreatmentWarningSlugs">
      <component
        v-if="!!treatmentWarningMap[componentSlug]"
        :key="componentSlug"
        :is="treatmentWarningMap[componentSlug]"
        v-model="treatmentsWarningDialogs[componentSlug]"
      />
    </template>

    <indicated-treatments-node
      v-model="treatments"
      :showAll="filter.medicine === 'all'"
      :coverageFilter="filter.coverage"
    />

    <select-printing-template-dialog
      v-if="dialog.printingTemplate.show"
      v-model="dialog.printingTemplate.show"
      type="receipt"
      v-bind="dialog.printingTemplate.data"
      @closed="dialog.printingTemplate = { show: false, data: {} }"
    />
  </box-node>
</template>

<script>
import { PropOptions, WatchOptionsWithHandler, Component } from 'vue'
import { ObjectLiteral, Computed, Method } from '@/_typedefs'
import MedicinePrescriptionFilter from './MedicinePrescriptionFilter.vue'
import ExpandableBoxNode from '@/components/NewAttendanceForm/FormNodes/ExpandableBox/ExpandableBoxNode.vue'
import BoxNode from '@/components/NewAttendanceForm/FormNodes/Box/BoxNode.vue'
import IndicatedTreatmentsNode from '@/components/NewAttendanceForm/FormNodes/IndicatedTreatments/IndicatedTreatmentsNode.vue'
import { cloneDeep, differenceBy, flatten, groupBy, isEmpty, uniq } from 'lodash'
import SelectComponent from '@/components/NewAttendanceForm/Questions/Basic/Select/Select.component.vue'
import SimpleTextInputComponent from '@/components/NewAttendanceForm/Questions/Basic/TextInput/SimpleTextInput.component.vue'
import Mepolizumabe40WarningVue from '../ConductWarning/Warnings/Mepolizumabe40Warning.vue'
import Mepolizumabe100WarningVue from '../ConductWarning/Warnings/Mepolizumabe100Warning.vue'
import Omalizumabe75WarningVue from '../ConductWarning/Warnings/Omalizumabe75Warning.vue'
import Omalizumabe150WarningVue from '../ConductWarning/Warnings/Omalizumabe150Warning.vue'
import DupilumabeWarningVue from '../ConductWarning/Warnings/DupilumabeWarning.vue'
import BenralizumabeWarningVue from '../ConductWarning/Warnings/BenralizumabeWarning.vue'
import DownloadFrame from '@/components/DownloadFrame.vue'

import { createNamespacedHelpers } from 'vuex'
import SelectPrintingTemplateDialog from '@/components/PrintingTemplate/SelectPrintingTemplateDialog.vue'

const { mapGetters: newAttendanceFormGetters } = createNamespacedHelpers('newAttendanceForm')

export default {
  components: {
    MedicinePrescriptionFilter,
    ExpandableBoxNode,
    BoxNode,
    IndicatedTreatmentsNode,
    SelectComponent,
    SimpleTextInputComponent,
    DownloadFrame,
    SelectPrintingTemplateDialog,
  },
  props: {
    /** @type {PropOptions<any>} */
    value: {
      type: Object,
      default: () => ({}),
    },
    /** @type {PropOptions<Record<string, string>>} */
    treatmentsWarnings: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    const treatmentsWarningDialogs = uniq(Object.values(this.treatmentsWarnings)).reduce(
      (acc, curr) => ({ ...acc, [curr]: false }),
      {},
    )

    return {
      /** @type {{coverage: string[], medicine: 'all' | 'bula'}} */
      filter: {},
      /** @type {Record<string, ObjectLiteral[]>} */
      treatments: {},
      /** @type {number[]} */
      selectedTreatments: [],
      /** @type {Record<number, {id: number?, prescription: string, first?: boolean, maintenance?: boolean}>} */
      selectedTreatmentsPosologies: {},
      unwatch: null,
      /** @type {Record<string, boolean>} */
      treatmentsWarningDialogs,
      dialog: {
        printingTemplate: {
          show: false,
          data: {},
        },
      },
    }
  },
  computed: {
    .../** @type {{storeTreatments: Computed<ObjectLiteral>}} */ (
      newAttendanceFormGetters([{ storeTreatments: 'treatments', attendance: 'attendance' }])
    ),
    /** @type {Computed<Record<string, number>>} */
    selectedQuantityPerGroup() {
      const treatmentsEntries = Object.entries(this.treatments)
      const selectedGrouped = groupBy(this.selectedTreatments, (treatment) =>
        treatmentsEntries
          .find(([_group, treatments]) => treatments.some((searchTreatment) => searchTreatment.id === treatment))
          ?.at(0),
      )

      return Object.fromEntries(Object.entries(selectedGrouped).map(([group, selected]) => [group, selected.length]))
    },
    /** @type {Computed<Record<number, ObjectLiteral>>} */
    flatTreatments() {
      return flatten(Object.values(this.treatments)).reduce(
        (acc, treatment) => ({ ...acc, [treatment.id]: treatment }),
        {},
      )
    },
    /** @type {Computed<Record<number, ObjectLiteral[]>>} */
    treatmentPosologies() {
      return Object.fromEntries(
        Object.entries(this.flatTreatments)
          .map(([treatmentId, treatment]) => [
            treatmentId,
            treatment.dosages.flatMap((dosage) => {
              const prescriptions = []
              if (dosage.firstPrescription) {
                prescriptions.push({ id: dosage.id, prescription: dosage.firstPrescription, first: true })
              }
              if (dosage.maintenancePrescription) {
                prescriptions.push({ id: dosage.id, prescription: dosage.maintenancePrescription, maintenance: true })
              }
              return prescriptions
            }),
          ])
          .filter(([_treatmentId, posologies]) => !isEmpty(posologies)),
      )
    },
    /** @type {Computed<string[]>} */
    uniqueTreatmentWarningSlugs() {
      return uniq(Object.values(this.treatmentsWarnings))
    },
    /** @type {Computed<Record<string, Component>>}  */
    treatmentWarningMap() {
      return {
        'mepolizumabe-40': Mepolizumabe40WarningVue,
        'mepolizumabe-100': Mepolizumabe100WarningVue,
        'omalizumabe-75': Omalizumabe75WarningVue,
        'omalizumabe-150': Omalizumabe150WarningVue,
        dupilumabe: DupilumabeWarningVue,
        benralizumabe: BenralizumabeWarningVue,
      }
    },
  },
  methods: {
    openDialog(dialog, data) {
      const dialogMap = {
        printingTemplate: ({ treatment }) => {
          this.dialog.printingTemplate = {
            show: true,
            data: {
              patientId: this.attendance.patient.id,
              text: `${treatment.medicine.name}: <br/> ${this.selectedTreatmentsPosologies[treatment.id].prescription}`,
            },
          }
        },
      }

      dialogMap[dialog]?.(data)
    },
    /** @type {Method<[medicineName: string, shouldChange: boolean]>} */
    toggleWarningIfPossible(medicineName, shouldChange) {
      if (!shouldChange) {
        return
      }

      const componentSlug = this.treatmentsWarnings[medicineName]
      if (!componentSlug || !(componentSlug in this.treatmentsWarningDialogs)) {
        return
      }

      this.treatmentsWarningDialogs[componentSlug] = !this.treatmentsWarningDialogs[componentSlug]
    },
    /** @type {Method<[treatmentId: number], string>} */
    getPrintTreatmentUrl(treatmentId) {
      return `${this.$util.apiURL()}/print-treatment-dosage/${treatmentId}?dosageText=${this.getPrintTreatmentBody(
        treatmentId,
      )}`
    },
    /** @type {Method<[treatmentId: number], string>} */
    getPrintTreatmentBody(treatmentId) {
      return this.selectedTreatmentsPosologies[treatmentId]?.prescription ?? ''
    },
    isEmpty,
  },
  watch: {
    /** @type {WatchOptionsWithHandler<Record<number, ObjectLiteral>>} */
    flatTreatments: {
      handler(val) {
        const treatmentsToBeDeleted = differenceBy(this.selectedTreatments, Object.keys(val), parseInt)
        if (!treatmentsToBeDeleted.length) {
          return
        }
        this.selectedTreatments = this.selectedTreatments.filter(
          (treatmentId) => !treatmentsToBeDeleted.includes(treatmentId),
        )
      },
      deep: true,
    },
    /** @type {WatchOptionsWithHandler<number[]>} */
    selectedTreatments: {
      handler(val) {
        const treatmentsToBeAdded = differenceBy(val, Object.keys(this.selectedTreatmentsPosologies), parseInt)
        for (const treatmentId of treatmentsToBeAdded) {
          this.$set(this.selectedTreatmentsPosologies, treatmentId, { id: null, prescription: '' })
        }

        const treatmentsToBeDeleted = differenceBy(Object.keys(this.selectedTreatmentsPosologies), val, parseInt)
        for (const treatmentId of treatmentsToBeDeleted) {
          this.$delete(this.selectedTreatmentsPosologies, treatmentId)
        }
      },
      deep: true,
    },
    /** @type {WatchOptionsWithHandler<Record<number, {id: number?, prescription: string, first?: boolean, maintenance?: boolean}>>} */
    selectedTreatmentsPosologies: {
      handler(val) {
        this.$emit('input', val)
      },
      deep: true,
    },
  },
  created() {
    this.selectedTreatments = Object.keys(this.value).map((it) => parseInt(it))
    this.selectedTreatmentsPosologies = cloneDeep(this.value)
  },
}
</script>

<style scoped>
.small-btn-icon.v-btn :deep(i) {
  font-size: 20px;
}
.small-btn-icon.v-btn {
  height: 24px;
  width: 24px;
}

.custom-checkbox {
  margin: 0;
  padding: 0;
}

.custom-checkbox :deep(label) {
  font-size: 14px;
  color: var(--v-neutral-lighten17);
}

.custom-checkbox :deep(.v-input__slot) {
  font-size: 14px;
}

.custom-checkbox :deep(i) {
  font-size: 20px;
}
.custom-checkbox :deep(i.mdi-checkbox-blank-outline) {
  font-size: 20px;
}

.custom-checkbox :deep(.v-input--selection-controls__ripple) {
  font-size: 20px;
}
</style>
